import { useCallback, useState } from 'react';
import { useShowAlert } from 'hooks';
import strings from 'strings';
import { getErrorMessage } from 'UI/utils';

const buildFormParams = formData => ({
  difficulty: formData?.difficulty || '',
  experience: formData?.experience?.id || null,
  industry: {
    id: formData?.specialty?.industry_id || null,
    title: formData?.specialty?.industry_title || ''
  },
  specialty: {
    id: formData?.specialty?.id || null,
    title: formData?.specialty?.title || ''
  },
  subspecialty: {
    id: formData?.subspecialty?.id || null,
    title: formData?.subspecialty?.title || ''
  },
  title: formData?.title || '',
  type: formData?.type || ''
});

const COPIES = strings.ai.generators.roleplay;

export const useRoleplay = ({
  extendedParams,
  generatePresentationFn,
  isFromManualSetup = false
}) => {
  const { showAlert } = useShowAlert();
  const [generatedAiData, setGeneratedAiData] = useState(null);
  const [uiState, setUiState] = useState({
    isLoading: false
  });

  const handleGenerateCallWithAi = useCallback(
    async formData => {
      try {
        const { presentationText = null, type = null } = extendedParams;
        if (!type) return;

        if (!presentationText?.trim() && isFromManualSetup) {
          showAlert({
            body: COPIES.listPage.manualDrawer.presentation.requiredError,
            title: COPIES.error.title,
            severity: 'error'
          });
          return;
        }

        setUiState(prev => ({ ...prev, isLoading: true }));

        const finalParams = {
          ...buildFormParams(formData),
          ...extendedParams
        };

        const { data } = await generatePresentationFn(finalParams);
        setGeneratedAiData(data);
      } catch (error) {
        showAlert({
          body: getErrorMessage(error, COPIES.error.body),
          title: COPIES.error.title,
          severity: 'error'
        });
      } finally {
        setUiState(prev => ({ ...prev, isLoading: false }));
      }
    },
    [extendedParams, generatePresentationFn, isFromManualSetup, showAlert]
  );

  const handleResetGeneratedData = () => setGeneratedAiData(null);

  return {
    generatedAiData,
    handleGenerateCallWithAi,
    handleResetGeneratedData,
    uiState
  };
};
